import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { getArchiveTimeline, getArchiveImages, getArchiveVideos, downloadArchiveXml } from '../../../http/complexesApi';
import { unixTimeToString } from '../../../utils/converters';
import { OrbitProgress } from 'react-loading-indicators';
import Viewer from 'react-viewer';
import { useSelector } from 'react-redux';

// Material-UI Core
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Button,
    ButtonGroup,
    Chip,
    Tooltip,
    Stack,
    LinearProgress,
    Fade,
    Slide,
    Zoom,
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputAdornment,
    alpha
} from '@mui/material';

// Material-UI Icons
import {
    AccessTime as AccessTimeIcon,
    ArrowBackIos as ArrowBackIosIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
    Image as ImageIcon,
    Videocam as VideocamIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    TouchApp as TouchAppIcon,
    WarningAmber as WarningAmberIcon,
    ErrorOutline as ErrorOutlineIcon,
    PlayArrow as PlayArrowIcon,
    ZoomIn as ZoomInIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';

// Словарь типов нарушений
const violationTypeNames = {
    '0': 'Без нарушения',
    '2': 'Превышение скорости',
    '31': 'Ремень',
    '32': 'Телефон',
    '4': 'Встречное движение',
    '10': 'Проезд на красный',
    '30': 'Стоп-линия',
};

const getViolationName = (type) => {
    if (!type) return null;
    return violationTypeNames[type] || `Тип ${type}`;
};

const ComplexArchive = ({ host, hostType }) => {
    const [timeline, setTimeline] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [mediaData, setMediaData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [videosLoading, setVideosLoading] = useState(false); // Отдельное состояние для загрузки видео
    const [timelineLoading, setTimelineLoading] = useState(true);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [currentMediaType, setCurrentMediaType] = useState('images');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [viewerVisible, setViewerVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [searchRegno, setSearchRegno] = useState('');
    const [filterViolation, setFilterViolation] = useState('');
    const [xmlDownloading, setXmlDownloading] = useState(false);
    const timelineRef = useRef();
    const mediaContainerRef = useRef();
    const videosAbortControllerRef = useRef(null); // Для отмены загрузки видео при смене времени

    const isDarkMode = useSelector(state => state.settings.darkMode);

    // Используем hostid для API archive
    const archiveId = useMemo(() => {
        return host?.host || null
    }, [host])

    const currentMediaArray = useMemo(() => {
        if (!mediaData) return [];
        return currentMediaType === 'images' ? (mediaData.images || []) : (mediaData.videos || []);
    }, [mediaData, currentMediaType]);

    const violationTypes = useMemo(() => {
        if (!timeline.length) return [];
        const types = new Set();
        timeline.forEach(item => {
            if (item.vPrViol) {
                types.add(item.vPrViol);
            }
        });
        return Array.from(types).sort();
    }, [timeline]);

    const currentRegno = useMemo(() => {
        if (!selectedTime || !timeline.length) return null;
        const item = timeline.find(t => t.time === selectedTime);
        return item?.vRegno || null;
    }, [selectedTime, timeline]);

    const currentViolation = useMemo(() => {
        if (!selectedTime || !timeline.length) return null;
        const item = timeline.find(t => t.time === selectedTime);
        return item?.vPrViol || null;
    }, [selectedTime, timeline]);

    const filteredTimeline = useMemo(() => {
        if (!timeline.length) return [];
        return timeline.filter(item => {
            const matchesRegno = !searchRegno || (item.vRegno && item.vRegno.toUpperCase().includes(searchRegno));
            const matchesViolation = !filterViolation || item.vPrViol === filterViolation;
            return matchesRegno && matchesViolation;
        });
    }, [timeline, searchRegno, filterViolation]);

    const getViewerImages = () => {
        if (!mediaData || !mediaData.images) return [];
        return mediaData.images.map((image, index) => ({
            src: image.data,
            alt: image.name || `Изображение ${index + 1}`
        }));
    };

    const handleKeyPress = useCallback((event) => {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;

        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                if (event.shiftKey) {
                    navigateTime('prev');
                } else {
                    navigateMedia('prev');
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (event.shiftKey) {
                    navigateTime('next');
                } else {
                    navigateMedia('next');
                }
                break;
            case '1':
                handleMediaTypeChange('images');
                break;
            case '2':
                handleMediaTypeChange('videos');
                break;
            case 'f':
            case 'F':
                toggleFullscreen();
                break;
            case 'Escape':
                if (isFullscreen) {
                    exitFullscreen();
                }
                break;
            default:
                break;
        }
    }, [isFullscreen]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    useEffect(() => {
        const loadTimeline = async () => {
            try {
                setTimelineLoading(true);
                const timelineData = await getArchiveTimeline(archiveId);
                setTimeline(timelineData);

                if (timelineData && timelineData.length > 0) {
                    setSelectedTime(timelineData[timelineData.length - 1].time);
                }
            } catch (error) {
                console.error('Ошибка загрузки временной шкалы:', error);
                setTimeline([]);
            } finally {
                setTimelineLoading(false);
            }
        };

        loadTimeline();
    }, [archiveId]);

    useEffect(() => {
        if (selectedTime) {
            // Отменяем предыдущую загрузку видео если она была
            if (videosAbortControllerRef.current) {
                videosAbortControllerRef.current.abort();
            }

            const loadMediaData = async () => {
                try {
                    setLoading(true);
                    setVideosLoading(false);

                    // Сначала быстро загружаем изображения
                    const imagesData = await getArchiveImages(archiveId, selectedTime);

                    // Сразу показываем изображения
                    setMediaData({
                        time: selectedTime,
                        images: imagesData.images || [],
                        videos: null // Видео ещё не загружены
                    });
                    setCurrentMediaIndex(0);
                    setLoading(false);

                    // Теперь загружаем видео в фоне
                    setVideosLoading(true);
                    const abortController = new AbortController();
                    videosAbortControllerRef.current = abortController;

                    try {
                        const videosData = await getArchiveVideos(archiveId, selectedTime, abortController.signal);

                        // Проверяем что запрос не был отменён и время не изменилось
                        if (!abortController.signal.aborted) {
                            setMediaData(prev => {
                                if (prev && prev.time === selectedTime) {
                                    return {
                                        ...prev,
                                        videos: videosData.videos || []
                                    };
                                }
                                return prev;
                            });
                        }
                    } catch (videoError) {
                        // Игнорируем ошибки отмены запроса
                        if (videoError.name !== 'AbortError' &&
                            videoError.name !== 'CanceledError' &&
                            videoError.code !== 'ERR_CANCELED') {
                            console.error('Ошибка загрузки видео:', videoError);
                        }
                    } finally {
                        // Устанавливаем videosLoading в false только если это текущий контроллер
                        if (videosAbortControllerRef.current === abortController) {
                            setVideosLoading(false);
                        }
                    }

                } catch (error) {
                    console.error('Ошибка загрузки изображений:', error);
                    setMediaData(null);
                    setLoading(false);
                }
            };

            loadMediaData();
        }

        // Cleanup при размонтировании или смене времени
        return () => {
            if (videosAbortControllerRef.current) {
                videosAbortControllerRef.current.abort();
            }
        };
    }, [selectedTime, archiveId]);

    useEffect(() => {
        setCurrentMediaIndex(0);
    }, [currentMediaType]);

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
    };

    const navigateTime = (direction) => {
        if (!filteredTimeline.length || !selectedTime) return;

        const currentIndex = filteredTimeline.findIndex(t => t.time === selectedTime);
        let newIndex;

        if (direction === 'prev' && currentIndex > 0) {
            newIndex = currentIndex - 1;
        } else if (direction === 'next' && currentIndex < filteredTimeline.length - 1) {
            newIndex = currentIndex + 1;
        } else {
            return;
        }

        setSelectedTime(filteredTimeline[newIndex].time);
    };

    const navigateMedia = (direction) => {
        if (!currentMediaArray.length) return;

        let newIndex;
        if (direction === 'prev' && currentMediaIndex > 0) {
            newIndex = currentMediaIndex - 1;
        } else if (direction === 'next' && currentMediaIndex < currentMediaArray.length - 1) {
            newIndex = currentMediaIndex + 1;
        } else {
            return;
        }

        setCurrentMediaIndex(newIndex);
    };

    const handleMediaTypeChange = (type) => {
        setCurrentMediaType(type);
    };

    const handleDownloadXml = async () => {
        if (!selectedTime) return;
        try {
            setXmlDownloading(true);
            const blob = await downloadArchiveXml(archiveId, selectedTime);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fileName = `${selectedTime}_${archiveId}_message.xml`;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Ошибка скачивания XML:', error);
        } finally {
            setXmlDownloading(false);
        }
    };

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            enterFullscreen();
        } else {
            exitFullscreen();
        }
    };

    const enterFullscreen = () => {
        if (mediaContainerRef.current) {
            if (mediaContainerRef.current.requestFullscreen) {
                mediaContainerRef.current.requestFullscreen();
            } else if (mediaContainerRef.current.webkitRequestFullscreen) {
                mediaContainerRef.current.webkitRequestFullscreen();
            } else if (mediaContainerRef.current.msRequestFullscreen) {
                mediaContainerRef.current.msRequestFullscreen();
            }
            setIsFullscreen(true);
        }
    };

    const exitFullscreen = () => {
        if (document.fullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
        setIsFullscreen(false);
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    if (timelineLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    backgroundColor: 'var(--background-default)'
                }}
            >
                <OrbitProgress variant="track-disc" dense color="var(--primary-color)" size="medium" />
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 1, md: 1.5 },
            p: { xs: 0.75, md: 1 },
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: 'var(--background-default)'
        }}>
            {/* Временная шкала слева */}
            <Paper
                elevation={0}
                sx={{
                    width: { xs: '100%', md: 260 },
                    maxHeight: { xs: '40%', md: 'none' },
                    minHeight: { xs: '30%', md: 'none' },
                    backgroundColor: 'var(--background-paper)',
                    border: `1px solid var(--border-default)`,
                    borderRadius: { xs: '8px', md: '12px' },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Заголовок */}
                <Box sx={{
                    p: { xs: 0.5, md: 1.5 },
                    borderBottom: `1px solid var(--border-default)`,
                    backgroundColor: 'var(--background-subtle)'
                }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 0.75, md: 1 }} sx={{ mb: { xs: 0.5, md: 1.5 } }}>
                        <AccessTimeIcon sx={{ fontSize: { xs: 16, md: 18 }, color: 'var(--primary-color)' }} />
                        <Typography sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.85rem' },
                            color: 'var(--text-primary)'
                        }}>
                            Временная шкала
                        </Typography>
                    </Stack>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', md: 'column' },
                        gap: 1,
                    }}>
                        {/* Поиск по номеру */}
                        <TextField
                            size="small"
                            placeholder="Поиск по номеру..."
                            value={searchRegno}
                            onChange={(e) => setSearchRegno(e.target.value.toUpperCase())}
                            sx={{
                                // mb: { xs: 0.2, md: 1 },
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isDarkMode ? 'var(--background-subtle)' : 'var(--background-paper)',
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    borderRadius: { xs: '6px', md: '8px' },
                                    color: 'var(--text-primary)',
                                    '& fieldset': { borderColor: 'var(--border-default)' },
                                    '&:hover fieldset': { borderColor: 'var(--primary-color)' },
                                    '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' }
                                },
                                '& .MuiOutlinedInput-input': {
                                    color: 'var(--text-primary)',
                                    '&::placeholder': {
                                        color: 'var(--text-muted)',
                                        opacity: 1
                                    }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'var(--text-muted)', fontSize: { xs: 14, md: 16 } }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchRegno && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchRegno('')} sx={{ color: 'var(--text-muted)', padding: { xs: '4px', md: '8px' } }}>
                                            <ClearIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Фильтр по типу нарушения */}
                        <FormControl size="small" sx={{ width: '100%' }}>
                            <Select
                                value={filterViolation}
                                onChange={(e) => setFilterViolation(e.target.value)}
                                displayEmpty
                                sx={{
                                    backgroundColor: isDarkMode ? 'var(--background-paper)' : 'var(--background-paper)',
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    borderRadius: { xs: '6px', md: '8px' },
                                    '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? 'var(--border-default)' : 'var(--border-default)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' }
                                }}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <FilterListIcon sx={{ color: 'var(--text-muted)', fontSize: { xs: 14, md: 16 } }} />
                                    </InputAdornment>
                                }
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
                                            backgroundImage: 'none',
                                            border: `1px solid ${isDarkMode ? '#30363d' : '#d0d7de'}`,
                                            borderRadius: { xs: '6px', md: '8px' },
                                            boxShadow: isDarkMode
                                                ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                                                : '0 8px 24px rgba(0, 0, 0, 0.15)',
                                            mt: 0.5,
                                            '& .MuiMenuItem-root': {
                                                fontSize: { xs: '0.7rem', md: '0.75rem' },
                                                minHeight: { xs: 28, md: 32 },
                                                color: isDarkMode ? '#f0f6fc' : '#24292f',
                                                '&:hover': {
                                                    backgroundColor: isDarkMode
                                                        ? 'rgba(177, 186, 196, 0.12)'
                                                        : 'rgba(208, 215, 222, 0.32)'
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: isDarkMode
                                                        ? 'rgba(88, 166, 255, 0.15)'
                                                        : 'rgba(9, 105, 218, 0.08)',
                                                    color: isDarkMode ? '#58a6ff' : '#0969da',
                                                    '&:hover': {
                                                        backgroundColor: isDarkMode
                                                            ? 'rgba(88, 166, 255, 0.2)'
                                                            : 'rgba(9, 105, 218, 0.12)'
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="">Все</MenuItem>
                                {violationTypes.map(type => (
                                    <MenuItem key={type} value={type}>{getViolationName(type)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Список времён */}
                <Box
                    ref={timelineRef}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: { xs: 0.5, md: 1 },
                        '&::-webkit-scrollbar': { width: { xs: '3px', md: '4px' } },
                        '&::-webkit-scrollbar-track': {
                            background: 'var(--background-subtle)',
                            borderRadius: '2px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'var(--border-default)',
                            borderRadius: '2px',
                            '&:hover': { background: 'var(--text-muted)' }
                        }
                    }}
                >
                    {filteredTimeline.length > 0 ? (
                        <Stack spacing={{ xs: 0.2, md: 0.5 }}>
                            {filteredTimeline.map((item, index) => (
                                <Box
                                    key={item.time}
                                    onClick={() => handleTimeSelect(item.time)}
                                    sx={{
                                        p: { xs: 0.3, md: 1 },
                                        cursor: 'pointer',
                                        borderRadius: { xs: '6px', md: '8px' },
                                        backgroundColor: selectedTime === item.time
                                            ? alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.15)
                                            : 'transparent',
                                        border: `1px solid ${selectedTime === item.time ? 'var(--primary-color)' : 'transparent'}`,
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            backgroundColor: selectedTime === item.time
                                                ? alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.15)
                                                : 'var(--hover-bg)'
                                        }
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={{ xs: 0.75, md: 1 }}>
                                        <Box sx={{
                                            width: { xs: 24, md: 28 },
                                            height: { xs: 24, md: 28 },
                                            borderRadius: { xs: '4px', md: '6px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: selectedTime === item.time
                                                ? 'var(--primary-color)'
                                                : 'var(--background-subtle)',
                                            color: selectedTime === item.time ? '#fff' : 'var(--text-secondary)',
                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                            fontWeight: 600
                                        }}>
                                            {index + 1}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{
                                                fontWeight: selectedTime === item.time ? 600 : 400,
                                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                                color: 'var(--text-primary)',
                                                lineHeight: 1.2
                                            }}>
                                                {unixTimeToString(item.time).split(' ')[1]}
                                            </Typography>
                                            <Stack direction="row" spacing={{ xs: 0.4, md: 0.5 }} alignItems="center" flexWrap="wrap">
                                                <Typography sx={{
                                                    fontSize: { xs: '0.6rem', md: '0.65rem' },
                                                    color: 'var(--text-muted)'
                                                }}>
                                                    {unixTimeToString(item.time).split(' ')[0].replace(',', '')}
                                                </Typography>
                                                <Chip
                                                    label={item.vRegno === 'unknown' ? "ГРЗ не определен" : item.vRegno}
                                                    size="small"
                                                    sx={{
                                                        height: { xs: 14, md: 16 },
                                                        fontSize: { xs: '0.55rem', md: '0.6rem' },
                                                        backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                                                        color: 'var(--primary-color)',
                                                        '& .MuiChip-label': { px: { xs: 0.4, md: 0.5 } }
                                                    }}
                                                />
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 4 }, color: 'var(--text-muted)' }}>
                            <ErrorOutlineIcon sx={{ fontSize: { xs: 32, md: 40 }, opacity: 0.5, mb: { xs: 0.75, md: 1 } }} />
                            <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                                Нет данных
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Футер временной шкалы */}
                {filteredTimeline.length > 0 && (
                    <Box sx={{
                        p: { xs: 0.5, md: 1.5 },
                        borderTop: `1px solid var(--border-default)`,
                        backgroundColor: 'var(--background-subtle)'
                    }}>
                        <LinearProgress
                            variant="determinate"
                            value={selectedTime ? (filteredTimeline.findIndex(t => t.time === selectedTime) / Math.max(filteredTimeline.length - 1, 1)) * 100 : 0}
                            sx={{
                                height: { xs: 3, md: 4 },
                                borderRadius: 2,
                                mb: { xs: 0.5, md: 1 },
                                backgroundColor: 'var(--border-default)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: 'var(--primary-color)',
                                    borderRadius: 2
                                }
                            }}
                        />
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={{ xs: 0.5, md: 0 }}>
                            <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.65rem' }, color: 'var(--text-muted)' }}>
                                {selectedTime ? filteredTimeline.findIndex(t => t.time === selectedTime) + 1 : 0} / {filteredTimeline.length}
                                {timeline.length !== filteredTimeline.length && ` (${timeline.length})`}
                            </Typography>
                            <Stack direction="row" spacing={{ xs: 0.4, md: 0.5 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => navigateTime('prev')}
                                    disabled={!filteredTimeline.length || filteredTimeline.findIndex(t => t.time === selectedTime) === 0}
                                    sx={{
                                        width: { xs: 24, md: 28 },
                                        height: { xs: 24, md: 28 },
                                        border: `1px solid var(--border-default)`,
                                        borderRadius: { xs: '4px', md: '6px' },
                                        color: 'var(--text-secondary)',
                                        backgroundColor: isDarkMode ? 'var(--background-paper)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'var(--hover-bg)',
                                            borderColor: 'var(--primary-color)'
                                        },
                                        '&:disabled': {
                                            opacity: 0.3,
                                            color: 'var(--text-muted)'
                                        }
                                    }}
                                >
                                    <ArrowBackIosIcon sx={{ fontSize: { xs: 10, md: 12 }, ml: 0.5 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => navigateTime('next')}
                                    disabled={!filteredTimeline.length || filteredTimeline.findIndex(t => t.time === selectedTime) === filteredTimeline.length - 1}
                                    sx={{
                                        width: { xs: 24, md: 28 },
                                        height: { xs: 24, md: 28 },
                                        border: `1px solid var(--border-default)`,
                                        borderRadius: { xs: '4px', md: '6px' },
                                        color: 'var(--text-secondary)',
                                        backgroundColor: isDarkMode ? 'var(--background-paper)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'var(--hover-bg)',
                                            borderColor: 'var(--primary-color)'
                                        },
                                        '&:disabled': {
                                            opacity: 0.3,
                                            color: 'var(--text-muted)'
                                        }
                                    }}
                                >
                                    <ArrowForwardIosIcon sx={{ fontSize: { xs: 10, md: 12 } }} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Box>
                )}
            </Paper>

            {/* Медиа проигрыватель */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {selectedTime ? (
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            backgroundColor: 'var(--background-paper)',
                            border: `1px solid var(--border-default)`,
                            borderRadius: { xs: '8px', md: '12px' }
                        }}
                    >
                        {/* Область медиа */}
                        {loading ? (
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: { xs: 250, md: 400 }
                            }}>
                                <OrbitProgress variant="track-disc" dense color="var(--primary-color)" size="medium" />
                            </Box>
                        ) : mediaData && (mediaData.images?.length > 0 || mediaData.videos?.length > 0) ? (
                            <>
                                <Box
                                    ref={mediaContainerRef}
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        position: 'relative',
                                        backgroundColor: '#0d1117',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: { xs: 250, md: 400 },
                                        ...(isFullscreen && {
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            width: '100vw',
                                            height: '100vh',
                                            zIndex: 9999
                                        })
                                    }}
                                >
                                    {currentMediaArray.length > 0 ? (
                                    currentMediaType === 'images' ? (
                                        <Fade in={true} timeout={300}>
                                            <Box
                                                onClick={() => {
                                                    setCurrentImageIndex(currentMediaIndex);
                                                    setViewerVisible(true);
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    maxHeight: '100%',
                                                    maxWidth: '100%',
                                                    // '&:hover .zoom-overlay': { opacity: 1 }
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={currentMediaArray[currentMediaIndex]?.data}
                                                    alt={`Изображение ${currentMediaIndex + 1}`}
                                                    sx={{
                                                        maxHeight: '100%',
                                                        maxWidth: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                                <Box
                                                    className="zoom-overlay"
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s ease'
                                                    }}
                                                >
                                                    <ZoomInIcon sx={{ color: 'white', fontSize: { xs: 36, md: 48 } }} />
                                                </Box>
                                            </Box>
                                        </Fade>
                                    ) : (
                                        <Fade in={true} timeout={300}>
                                            <Box
                                                component="video"
                                                controls
                                                key={currentMediaIndex}
                                                sx={{
                                                    maxHeight: '100%',
                                                    maxWidth: '100%'
                                                }}
                                            >
                                                <source src={currentMediaArray[currentMediaIndex]?.data} type="video/mp4" />
                                            </Box>
                                        </Fade>
                                    )
                                    ) : (
                                        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1, py: 4 }}>
                                            <OrbitProgress variant="track-disc" dense color="var(--primary-color)" size="medium" />
                                            <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)' }}>
                                                Загрузка видео...
                                            </Typography>
                                            <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, color: 'var(--text-muted)' }}>
                                                Видео загружаются в фоновом режиме
                                            </Typography>
                                        </Stack>
                                    )}

                                    {/* Навигация по медиа */}
                                    {currentMediaArray.length > 1 && (
                                        <>
                                            <IconButton
                                                onClick={() => navigateMedia('prev')}
                                                disabled={currentMediaIndex === 0}
                                                sx={{
                                                    position: 'absolute',
                                                    left: { xs: 8, md: 16 },
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                                    color: 'white',
                                                    width: { xs: 36, md: 44 },
                                                    height: { xs: 36, md: 44 },
                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                                    '&:disabled': { opacity: 0.3, color: 'white' },
                                                    '& .MuiSvgIcon-root': { fontSize: { xs: 20, md: 24 } }
                                                }}
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => navigateMedia('next')}
                                                disabled={currentMediaIndex === currentMediaArray.length - 1}
                                                sx={{
                                                    position: 'absolute',
                                                    right: { xs: 8, md: 16 },
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                                    color: 'white',
                                                    width: { xs: 36, md: 44 },
                                                    height: { xs: 36, md: 44 },
                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                                    '&:disabled': { opacity: 0.3, color: 'white' },
                                                    '& .MuiSvgIcon-root': { fontSize: { xs: 20, md: 24 } }
                                                }}
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </>
                                    )}

                                    {/* Инфо в fullscreen */}
                                    {isFullscreen && (
                                        <Slide direction="up" in={true} timeout={300}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                                                    color: 'white',
                                                    p: { xs: 2, md: 3 }
                                                }}
                                            >
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={{ xs: 1, md: 0 }}>
                                                    <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 2 }}>
                                                        {currentMediaType === 'images' ? (
                                                            <ImageIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
                                                        ) : (
                                                            <VideocamIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
                                                        )}
                                                        <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                            {currentMediaIndex + 1} / {currentMediaArray.length}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{unixTimeToString(selectedTime)}</Typography>
                                                </Stack>
                                            </Box>
                                        </Slide>
                                    )}
                                </Box>

                                {/* Футер медиа проигрывателя */}
                                {!isFullscreen && (
                                    <Box sx={{
                                        p: { xs: 0.5, md: 1.5 },
                                        borderTop: `1px solid var(--border-default)`,
                                        backgroundColor: 'var(--background-subtle)'
                                    }}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={{ xs: 1, md: 0 }}>
                                            {/* Левая часть - время и метаданные */}
                                            <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 1.5 }} flexWrap="wrap">
                                                <Stack direction="row" alignItems="center" spacing={{ xs: 0.4, md: 0.5 }}>
                                                    <ScheduleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--text-muted)' }} />
                                                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, color: 'var(--text-primary)', fontWeight: 500 }}>
                                                        {unixTimeToString(selectedTime)}
                                                    </Typography>
                                                </Stack>
                                                {currentRegno && (
                                                    <Chip
                                                        label={currentRegno === 'unknown' ? 'ГРЗ не определен' : currentRegno}
                                                        size="small"
                                                        sx={{
                                                            height: { xs: 20, md: 22 },
                                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                            fontWeight: 600,
                                                            backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                                                            color: 'var(--primary-color)',
                                                            border: `1px solid ${alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.3)}`
                                                        }}
                                                    />
                                                )}
                                                {currentViolation && (
                                                    <Chip
                                                        label={getViolationName(currentViolation)}
                                                        size="small"
                                                        sx={{
                                                            height: { xs: 20, md: 22 },
                                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                            backgroundColor: alpha(isDarkMode ? '#bc8cff' : '#8250df', 0.1),
                                                            color: 'var(--secondary-color)',
                                                            border: `1px solid ${alpha(isDarkMode ? '#bc8cff' : '#8250df', 0.3)}`
                                                        }}
                                                    />
                                                )}
                                                {currentMediaArray[currentMediaIndex]?.size && (
                                                    <Typography sx={{
                                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                        color: 'var(--text-muted)',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        {(currentMediaArray[currentMediaIndex].size / 1024 / 1024).toFixed(2)} MB
                                                    </Typography>
                                                )}
                                            </Stack>

                                            {/* Правая часть - кнопки */}
                                            <Stack direction="row" alignItems="center" spacing={{ xs: 0.75, md: 1 }} flexWrap="wrap">
                                                {/* Миниатюры */}
                                                {currentMediaArray.length > 1 && (
                                                    <Stack direction="row" spacing={{ xs: 0.4, md: 0.5 }} sx={{ mr: { xs: 0.75, md: 1 } }}>
                                                        {currentMediaArray.slice(0, 5).map((media, index) => (
                                                            <Box
                                                                key={index}
                                                                onClick={() => setCurrentMediaIndex(index)}
                                                                sx={{
                                                                    width: { xs: 28, md: 36 },
                                                                    height: { xs: 20, md: 26 },
                                                                    borderRadius: { xs: '3px', md: '4px' },
                                                                    overflow: 'hidden',
                                                                    cursor: 'pointer',
                                                                    border: `2px solid ${index === currentMediaIndex ? 'var(--primary-color)' : 'var(--border-default)'}`,
                                                                    opacity: index === currentMediaIndex ? 1 : 0.7,
                                                                    transition: 'all 0.15s ease',
                                                                    '&:hover': { opacity: 1 }
                                                                }}
                                                            >
                                                                {currentMediaType === 'images' ? (
                                                                    <Box
                                                                        component="img"
                                                                        src={media.data}
                                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <Box sx={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        backgroundColor: '#000'
                                                                    }}>
                                                                        <PlayArrowIcon sx={{ fontSize: { xs: 10, md: 14 }, color: 'white' }} />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        ))}
                                                        {currentMediaArray.length > 5 && (
                                                            <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.65rem' }, color: 'var(--text-muted)', alignSelf: 'center' }}>
                                                                +{currentMediaArray.length - 5}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                )}

                                                {/* Переключатель типа медиа */}
                                                <ButtonGroup size="small" sx={{ mr: { xs: 0.4, md: 0.5 } }}>
                                                    <Button
                                                        variant={currentMediaType === 'images' ? 'contained' : 'outlined'}
                                                        onClick={() => handleMediaTypeChange('images')}
                                                        sx={{
                                                            minWidth: { xs: 32, md: 40 },
                                                            px: { xs: 0.75, md: 1 },
                                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                            borderColor: 'var(--border-default)',
                                                            ...(currentMediaType === 'images' && {
                                                                backgroundColor: 'var(--primary-color)',
                                                                '&:hover': { backgroundColor: 'var(--primary-dark)' }
                                                            })
                                                        }}
                                                    >
                                                        <ImageIcon sx={{ fontSize: { xs: 14, md: 16 }, mr: { xs: 0.4, md: 0.5 } }} />
                                                        {mediaData?.images?.length || 0}
                                                    </Button>
                                                    <Tooltip title={videosLoading ? "Загрузка видео..." : ""}>
                                                        <Button
                                                            variant={currentMediaType === 'videos' ? 'contained' : 'outlined'}
                                                            onClick={() => handleMediaTypeChange('videos')}
                                                            disabled={videosLoading && (!mediaData?.videos || mediaData.videos.length === 0)}
                                                            sx={{
                                                                minWidth: { xs: 32, md: 40 },
                                                                px: { xs: 0.75, md: 1 },
                                                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                                borderColor: 'var(--border-default)',
                                                                position: 'relative',
                                                                ...(currentMediaType === 'videos' && {
                                                                    backgroundColor: 'var(--primary-color)',
                                                                    '&:hover': { backgroundColor: 'var(--primary-dark)' }
                                                                })
                                                            }}
                                                        >
                                                            <VideocamIcon sx={{ fontSize: { xs: 14, md: 16 }, mr: { xs: 0.4, md: 0.5 } }} />
                                                            {videosLoading ? (
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        display: 'inline-block',
                                                                        width: 12,
                                                                        height: 12,
                                                                        border: '2px solid',
                                                                        borderColor: 'currentColor',
                                                                        borderTopColor: 'transparent',
                                                                        borderRadius: '50%',
                                                                        animation: 'spin 1s linear infinite',
                                                                        '@keyframes spin': {
                                                                            '0%': { transform: 'rotate(0deg)' },
                                                                            '100%': { transform: 'rotate(360deg)' }
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                mediaData?.videos?.length || 0
                                                            )}
                                                        </Button>
                                                    </Tooltip>
                                                </ButtonGroup>

                                                {/* Скачать XML */}
                                                {hostType === 'crossroad' && (
                                                    <Tooltip title={xmlDownloading ? "Загрузка XML..." : "Скачать XML"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleDownloadXml}
                                                            disabled={xmlDownloading}
                                                            sx={{
                                                                width: { xs: 28, md: 32 },
                                                                height: { xs: 28, md: 32 },
                                                                border: `1px solid var(--border-default)`,
                                                                borderRadius: { xs: '4px', md: '6px' },
                                                                color: 'var(--primary-color)',
                                                                '&:hover': { backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1) }
                                                            }}
                                                        >
                                                            {xmlDownloading ? (
                                                                <Box
                                                                    sx={{
                                                                        width: { xs: 16, md: 18 },
                                                                        height: { xs: 16, md: 18 },
                                                                        border: '2px solid',
                                                                        borderColor: 'currentColor',
                                                                        borderTopColor: 'transparent',
                                                                        borderRadius: '50%',
                                                                        animation: 'spin 1s linear infinite',
                                                                        '@keyframes spin': {
                                                                            '0%': { transform: 'rotate(0deg)' },
                                                                            '100%': { transform: 'rotate(360deg)' }
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <DownloadIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {/* Полноэкранный режим */}
                                                <Tooltip title="Полный экран">
                                                    <IconButton
                                                        size="small"
                                                        onClick={toggleFullscreen}
                                                        sx={{
                                                            width: { xs: 28, md: 32 },
                                                            height: { xs: 28, md: 32 },
                                                            border: `1px solid var(--border-default)`,
                                                            borderRadius: { xs: '4px', md: '6px' },
                                                            color: 'var(--text-secondary)',
                                                            '&:hover': { backgroundColor: 'var(--hover-bg)' }
                                                        }}
                                                    >
                                                        {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: { xs: 16, md: 18 } }} /> : <FullscreenIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: { xs: 2, md: 4 },
                                color: 'var(--text-muted)'
                            }}>
                                {/* Если выбраны видео и они ещё загружаются - показываем индикатор */}
                                {currentMediaType === 'videos' && videosLoading ? (
                                    <>
                                        <OrbitProgress variant="track-disc" dense color="var(--primary-color)" size="medium" />
                                        <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mt: 2, mb: 0.5 }}>
                                            Загрузка видео...
                                        </Typography>
                                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                                            Видео загружаются в фоновом режиме
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <WarningAmberIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--warning-color)', mb: { xs: 1.5, md: 2 }, opacity: 0.7 }} />
                                        <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                                            Медиа не найдены
                                        </Typography>
                                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                                            Нет {currentMediaType === 'images' ? 'изображений' : 'видео'} для выбранного времени
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}
                    </Paper>
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 2, md: 4 },
                            backgroundColor: 'var(--background-paper)',
                            border: `1px solid var(--border-default)`,
                            borderRadius: { xs: '8px', md: '12px' }
                        }}
                    >
                        <TouchAppIcon sx={{ fontSize: { xs: 48, md: 64 }, color: 'var(--primary-color)', mb: { xs: 1.5, md: 2 }, opacity: 0.7 }} />
                        <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 600, color: 'var(--text-primary)', mb: 0.5 }}>
                            Выберите время
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                            Кликните на запись слева для просмотра
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* React Viewer */}
            <Viewer
                zIndex={9999}
                visible={viewerVisible}
                onClose={() => setViewerVisible(false)}
                images={getViewerImages()}
                activeIndex={currentImageIndex}
                onChange={(activeImage, activeIndex) => setCurrentImageIndex(activeIndex)}
            />
        </Box>
    );
};

export default ComplexArchive;
